import { useEffect, useState, useCallback } from "react";
import api from "./api";
import { applyCurrencyMethodGate, createRazorpayCheckout } from "./razorpayOptions";
import { useCurrency } from "../context/CurrencyContext";

const RZP_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RZP_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = RZP_SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/**
 * Cheap, deterministic 16-char fingerprint of a birth-details object so
 * the client knows whether a given chart is unlocked *before* hitting the API.
 * Must mirror the backend's `chart_fingerprint` (tool_unlocks.py) — we use a
 * lightweight stand-in, since the *real* gate lives server-side.
 *
 * For the FE we just pass the canonical string to the unlock-status endpoint;
 * the backend hashes it the same way and looks up.
 */
export function birthDetailsToFingerprint(birth) {
  // Send the *canonical raw inputs* — backend will hash them.
  // We don't need to compute the hash on the client at all; we call the
  // status endpoint with a stringified payload.
  // Returning a stable placeholder so the hook can cache by this value.
  const date  = (birth?.date  || "").trim();
  const time  = (birth?.time  || "").trim();
  const place = (birth?.place || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
  const tz    = (birth?.timezone || "").trim();
  // SHA-256 in browsers requires a Promise; for caching we just use a plain key.
  return `${date}|${time}|${place}|${tz}`;
}

/**
 * Hook: tracks unlock status for (tool, birthDetails) for the currently
 * signed-in customer. Re-fetches whenever birthDetails change.
 * Returns: { loading, unlocked, walletBalance, unlockPrice, refresh, payWithWallet, payWithRazorpay }
 */
export function useToolUnlock({ tool, birthDetails, user, onSuccess, report }) {
  // Pull the active display currency from the global context so non-INR
  // visitors get a USD-denominated Razorpay order (which routes the popup
  // to PayPal + international card only — no Indian wallets / UPI / etc.).
  const { currency } = useCurrency();
  const [state, setState] = useState({
    // Default to loading=true so PaywallGate hides the stale ₹333+₹0 stub
    // until /tools/price (or /tools/unlock-status) returns. Without this,
    // first paint flashes "Wallet has ₹0 — not enough" + "Pay ₹333 via
    // Razorpay" disabled, then auto-resolves to the real (often USD)
    // price + balance a few hundred ms later.
    loading: true,
    unlocked: false,
    walletBalance: 0,
    unlockPrice: 333,
    fingerprint: null,
  });
  const [paying, setPaying] = useState(false);

  const fpCacheKey = birthDetails ? birthDetailsToFingerprint(birthDetails) : null;

  const refresh = useCallback(async () => {
    if (!user || !birthDetails?.date || !birthDetails?.time) {
      // Still fetch the per-tool price so the paywall card shows the right amount
      // even before the user signs in or submits birth-details.
      try {
        const { data } = await api.get(`/tools/price?tool=${encodeURIComponent(tool)}`);
        setState((s) => ({ ...s, loading: false, unlocked: false, unlockPrice: data.unlock_price ?? s.unlockPrice }));
      } catch (e) {
        setState((s) => ({ ...s, loading: false, unlocked: false }));
      }
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    try {
      // First hash on the server by submitting a "status" call with raw birth_details
      // — endpoint accepts only fingerprint, so we compute via a no-op fetch helper:
      // Simplest path: POST a fingerprint-derivation call. Backend gives us the canonical
      // fingerprint by re-using its `chart_fingerprint` (we use the unlock-status endpoint
      // with the FE-side canonical key, which the backend matches because both apply the
      // same normalisation rules: lowercase + alphanumerics-only place + raw date/time/tz).
      // FE-side mimics backend logic exactly to produce the same hash:
      const fp = await sha256Hex16(fpCacheKey);
      const { data } = await api.get(`/tools/unlock-status?tool=${encodeURIComponent(tool)}&fingerprint=${fp}`);
      setState({
        loading: false,
        unlocked: !!data.unlocked,
        walletBalance: data.wallet_balance ?? 0,
        unlockPrice: data.unlock_price ?? 333,
        fingerprint: fp,
      });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, unlocked: false }));
    }
  }, [user, tool, fpCacheKey]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const payWithWallet = async () => {
    if (paying) return;
    setPaying(true);
    try {
      const { data } = await api.post("/tools/unlock/wallet", { tool, birth_details: birthDetails, report });
      setState((s) => ({ ...s, unlocked: true, walletBalance: data.wallet_balance ?? s.walletBalance }));
      onSuccess?.("wallet");
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Could not unlock from wallet." };
    } finally {
      setPaying(false);
    }
  };

  const payWithRazorpay = async ({ prefill } = {}) => {
    if (paying) return;
    setPaying(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) {
        setPaying(false);
        return { ok: false, error: "Could not load Razorpay. Check your connection." };
      }
      const { data: order } = await api.post("/tools/unlock/razorpay/order", {
        tool, birth_details: birthDetails, report,
        display_currency: currency,
      });
      return new Promise((resolve) => {
        // Tracks whether `handler` already settled (success path). If yes
        // we DON'T run reconcile on dismiss — handler already credited
        // the unlock and the popup just closed normally.
        let handlerFired = false;
        // Last-chance reconcile when the popup closes without the handler
        // firing (PayPal redirect, transient network, accidental close).
        // Calls /reconcile which independently queries Razorpay for any
        // captured payment on this order and unlocks if one exists.
        const tryReconcile = async () => {
          try {
            await api.post("/tools/unlock/razorpay/reconcile", {
              razorpay_order_id: order.order_id,
            });
            setState((s) => ({ ...s, unlocked: true }));
            onSuccess?.("razorpay-reconcile");
            return true;
          } catch (_) {
            return false;
          }
        };
        const rzp = createRazorpayCheckout({
          key: order.key_id,
          amount: order.amount_paise,
          currency: order.currency,
          name: "AstroKuber",
          description: `Unlock · ${order.tool_label}`,
          order_id: order.order_id,
          prefill: prefill || order.prefill || {},
          theme: { color: "#3F2D1B" },
          modal: {
            ondismiss: async () => {
              // If the handler already credited, dismiss is just the
              // popup closing post-success — bail out fast.
              if (handlerFired) { setPaying(false); return; }
              // Otherwise try to reconcile in case the customer DID pay
              // (PayPal/wallet redirect can swallow the handler callback).
              const recovered = await tryReconcile();
              setPaying(false);
              resolve(recovered
                ? { ok: true, recovered: true }
                : { ok: false, error: "Payment cancelled." });
            },
          },
          handler: async (response) => {
            handlerFired = true;
            try {
              await api.post("/tools/unlock/razorpay/verify", {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              setState((s) => ({ ...s, unlocked: true }));
              onSuccess?.("razorpay");
              resolve({ ok: true });
            } catch (e) {
              // Verify failed (amount mismatch, signature, network) — try
              // reconciling against the gateway one more time before we
              // surface an error to the user.
              const recovered = await tryReconcile();
              if (recovered) {
                resolve({ ok: true, recovered: true });
              } else {
                resolve({ ok: false, error: e?.response?.data?.detail || "Verification failed." });
              }
            } finally {
              setPaying(false);
            }
          },
        });
        rzp.open();
      });
    } catch (e) {
      setPaying(false);
      return { ok: false, error: e?.response?.data?.detail || "Could not start payment." };
    }
  };

  return { ...state, paying, refresh, payWithWallet, payWithRazorpay };
}

// ---------------- helpers ----------------

async function sha256Hex16(str) {
  const enc = new TextEncoder().encode(str.toLowerCase());
  const buf = await window.crypto.subtle.digest("SHA-256", enc);
  const bytes = new Uint8Array(buf);
  let hex = "";
  for (let i = 0; i < bytes.length; i++) hex += bytes[i].toString(16).padStart(2, "0");
  return hex.slice(0, 16);
}
